import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import { 
  Settings, 
  ArrowLeft, 
  Loader2,
  Box,
  Zap,
  Layers,
  Globe,
  Anchor 
} from 'lucide-react';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

const Category = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 100]);
  const opacityHero = useTransform(scrollY, [0, 400], [1, 0]);

  useEffect(() => { 
    window.scrollTo(0, 0);
    const fetchCategory = async () => {
      try { 
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/bookings/categories/${id}`);  
        setCategory(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching category details", error);
        setLoading(false);
      }
    };
    fetchCategory();
  }, [id]);

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin text-orange-500" size={40} />
          <p className="text-slate-400 font-bold tracking-widest text-[10px] uppercase">Syncing Data...</p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center px-6 text-center">
        <h2 className="text-lg font-bold mb-4 text-slate-900">Category Not Found</h2>
        <Link to="/" className="bg-slate-900 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 text-xs">
          <ArrowLeft size={16} /> Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans selection:bg-orange-500 selection:text-white overflow-x-hidden">
      <Header />

      {/* 1. HERO SECTION */}
      <section className="relative pt-28 pb-12 md:pt-40 md:pb-24 bg-slate-900 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-orange-500/5 skew-x-12 translate-x-32 hidden lg:block" />
        <motion.div 
          style={{ y: y1, opacity: opacityHero }}
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="group text-orange-500 flex items-center gap-2 mb-4 font-bold hover:text-orange-400 transition-all text-[9px] md:text-[11px] uppercase tracking-[0.3em]">
              <ArrowLeft size={12} /> Explore Collection
            </Link>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white uppercase italic tracking-tighter leading-[0.9] mb-6">
              {category.categoryName} <br />
              <span className="text-orange-500 not-italic">SERIES</span>
            </h1>
            <motion.div 
              initial={{ width: 0 }} 
              whileInView={{ width: 80 }} 
              viewport={{ once: false }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="h-2 bg-orange-500 rounded-full" 
            />
          </motion.div>
        </motion.div>
      </section>

      {/* 2. MAIN CONTENT GRID */}
      <section className="py-12 md:py-20 max-w-7xl mx-auto px-6"> 
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          {/* IMAGE BLOCK */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 30 }} 
            whileInView={{ opacity: 1, scale: 1, y: 0 }} 
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-6 w-full sticky top-32 mb-8 lg:mb-0"
          >
            <motion.div 
              whileHover={{ y: -8 }}
              className="relative aspect-[4/5] md:aspect-square lg:aspect-[3/4] overflow-hidden rounded-[1.5rem] bg-slate-50 border-[6px] md:border-[12px] border-white shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
            >
              <img 
                src={category.imageId} 
                alt={category.categoryName} 
                className="w-full h-full object-cover transform transition-transform duration-1000 hover:scale-105" 
              />
            </motion.div>
          </motion.div>

          {/* DESCRIPTION & SPECS */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 flex flex-col pt-2"
          >
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-6 w-1 bg-orange-500 rounded-full" />
                <h3 className="text-orange-500 text-xs md:text-sm font-black uppercase tracking-[0.2em]">
                  {category.categoryName} Series
                </h3>
              </div>
              <h2 className="text-slate-900 text-xl md:text-2xl font-black uppercase tracking-tight italic mb-6">About Product</h2>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed font-medium bg-slate-50 p-6 md:p-8 rounded-[1.2rem] italic shadow-inner border border-slate-100">
                {category.categoryDescription}
              </p>
            </div>

            {/* TECHNICAL SPECS */}
            <div className="mb-10">
              <h2 className="text-slate-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                <Settings size={14} /> Technical Specifications
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {category.features?.map((feature, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: false }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white border border-slate-100 p-4 md:p-5 rounded-xl border-l-2 border-l-slate-200 hover:border-l-orange-500 transition-all duration-300 shadow-sm"
                  >
                    <span className="text-slate-400 text-[8px] md:text-[9px] font-black uppercase tracking-widest block mb-1">
                      {feature.specType}
                    </span>
                    <p className="text-sm md:text-base font-black text-slate-900 leading-tight">
                      {feature.specValue}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* PRODUCT BUTTON */}
            <div className="flex justify-start items-center mt-4">
                <Link to="/products" state={{ categoryId: category._id }} className="w-full md:w-auto">
                <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="group relative w-full md:w-[240px] bg-slate-900 text-white py-4 px-8 rounded-xl shadow-xl overflow-hidden transition-all"
                >
                    <div className="absolute inset-0 bg-orange-500 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500 ease-out" />
                    <span className="relative z-10 flex items-center justify-center gap-3 text-sm font-black uppercase tracking-[0.1em]">
                    View Models <Box size={16} />
                    </span>
                </motion.button>
                </Link>
                <div className="hidden lg:block ml-8 h-[1px] flex-grow bg-slate-100" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3. ICON FEATURES */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            className="text-3xl md:text-4xl font-black text-slate-900 mb-6 uppercase italic tracking-tighter"
          >
            Core Systems
          </motion.h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
            {[
              { label: "Crane Control", icon: <Anchor size={22}/> },
              { label: "Flow Control", icon: <Anchor size={22}/> },
              { label: "Control Valve", icon: <Anchor size={22}/> },
              { label: "Eco Quality", icon: <Anchor size={22}/> }
            ].map((item, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ delay: idx * 0.1 }}
                className="flex flex-col items-center group cursor-default"
              >
                <div className="w-16 h-16 bg-white rounded-[1.2rem] flex items-center justify-center text-orange-500 mb-5 group-hover:bg-orange-500 group-hover:text-white group-hover:rotate-6 transition-all duration-500 shadow-lg shadow-slate-200">
                   {item.icon}
                </div>
                <p className="text-[10px] md:text-xs font-black text-slate-800 uppercase tracking-[0.2em]">
                  {item.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. ADVANTAGES SECTION */}
      <section className="py-20 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
            {[
              { title: "Heavy-Duty", icon: <Zap size={24}/>, desc: "Reinforced chassis integration for maximum lifting stability.", bg: "bg-slate-50 text-slate-900" },
              { title: "Innovative", icon: <Layers size={24}/>, desc: "Ergonomic control suite designed for operator precision.", bg: "bg-slate-900 text-white" },
              { title: "Versatility", icon: <Globe size={24}/>, desc: "Seamless operation across maritime and construction sites.", bg: "bg-slate-50 text-slate-900" }
            ].map((card, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                whileHover={{ y: -8 }} 
                className={`p-8 rounded-[1.5rem] ${card.bg} border border-slate-100 shadow-md transition-all duration-300`}
              >
                <div className={`w-12 h-12 ${i === 1 ? 'bg-white text-slate-900' : 'bg-orange-500 text-white'} rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                  {card.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-black uppercase tracking-tighter italic mb-3">{card.title}</h3>
                <p className="text-xs md:text-sm opacity-70 leading-relaxed font-medium">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Category;